import type { ReactNode } from "react";

import { Badge, Card, Heading, KeyValue, KeyValueList, Page, Skeleton, Text, TopBar } from "../ds";
import { useSesion } from "../sesion";
import s from "./Configuracion.module.css";

function Integracion({ titulo, ok, estadoOk = "Conectado", estadoNo = "Falta configurar", children, pie }: { titulo: string; ok: boolean; estadoOk?: string; estadoNo?: string; children: ReactNode; pie?: ReactNode }) {
  return (
    <Card padding="lg">
      <div className={s.cabecera}>
        <Heading level={2} size="sm">
          {titulo}
        </Heading>
        <Badge tone={ok ? "success" : "warning"} size="md" dot>
          {ok ? estadoOk : estadoNo}
        </Badge>
      </div>
      <KeyValueList tone="plain" layout="stacked">
        {children}
      </KeyValueList>
      {pie ? <div className={s.pie}>{pie}</div> : null}
    </Card>
  );
}

const Var = ({ children }: { children: ReactNode }) => <code className={s.var}>{children}</code>;

export function Configuracion() {
  const { sesion } = useSesion();
  if (!sesion) {
    return (
      <>
        <TopBar title="Configuración" />
        <Page width="content">
          <Skeleton lines={8} />
        </Page>
      </>
    );
  }
  const i = sesion.integraciones;
  const cw = i.chatwoot;
  return (
    <>
      <TopBar title="Configuración" subtitle="Qué está conectado y con qué variables de entorno se cambia. Se configura en el servidor, no desde acá." />
      <Page width="content">
        <div className={s.grilla}>
          <Integracion
            titulo="Chatwoot"
            ok={cw.configurado && cw.bandeja}
            pie={
              <Text size="xs" tone="muted">
                Variables: <Var>CHATWOOT_URL</Var> <Var>CHATWOOT_ACCOUNT_ID</Var> <Var>CHATWOOT_API_TOKEN</Var> <Var>CHATWOOT_INBOX_ID</Var>. El token es el de un agente, en Chatwoot › Perfil › Token de acceso.
              </Text>
            }
          >
            <KeyValue label="Servidor">{cw.url ?? "–"}</KeyValue>
            <KeyValue label="Cuenta" mono>
              {cw.cuenta ?? "–"}
            </KeyValue>
            <KeyValue label="Bandeja de WhatsApp">{cw.bandeja ? "Configurada" : "Falta CHATWOOT_INBOX_ID"}</KeyValue>
          </Integracion>

          <Integracion
            titulo="Respuestas automáticas"
            ok={cw.webhook}
            estadoOk="Webhook listo"
            estadoNo="Sin webhook"
            pie={
              <Text size="xs" tone="muted">
                En Chatwoot › Ajustes › Integraciones › Webhooks, agregá la URL de arriba con el evento “Mensaje creado”. El token es <Var>CHATWOOT_WEBHOOK_TOKEN</Var>: una clave larga que inventás vos.
              </Text>
            }
          >
            <KeyValue label="URL para Chatwoot" mono>
              {cw.webhookUrl ?? "Falta BAJA_BASE_URL (la URL pública del servidor)"}
            </KeyValue>
            <KeyValue label="Qué hace">Cuando una inmobiliaria contesta por WhatsApp, pasa sola a “Respondió” y se frena el email.</KeyValue>
          </Integracion>

          <Integracion
            titulo="Envío de WhatsApp"
            ok={sesion.envio.modo === "plantilla"}
            estadoOk="Plantilla de Meta"
            estadoNo="Texto libre"
            pie={
              <Text size="xs" tone="muted">
                Con la API oficial de WhatsApp, cargá <Var>WHATSAPP_PLANTILLA</Var> con el nombre aprobado en Meta y, si difiere, <Var>WHATSAPP_PLANTILLA_IDIOMA</Var> y <Var>WHATSAPP_PLANTILLA_TEXTO</Var>. Sin ellas sale texto libre, que sólo sirve con un número no oficial.
              </Text>
            }
          >
            <KeyValue label="Modo">{sesion.envio.modo === "plantilla" ? "Primer mensaje como plantilla aprobada" : "Sale el borrador tal cual"}</KeyValue>
            <KeyValue label="Plantilla" mono>
              {sesion.envio.plantilla ? `${sesion.envio.plantilla.nombre} (${sesion.envio.plantilla.idioma})` : "–"}
            </KeyValue>
            <KeyValue label="Puntaje mínimo" mono>
              {sesion.umbrales.whatsapp} para WhatsApp · {sesion.umbrales.email} para email
            </KeyValue>
          </Integracion>

          <Integracion titulo="Google Places" ok={i.places.configurado} pie={<Text size="xs" tone="muted">Variable: <Var>GOOGLE_PLACES_API_KEY</Var>. El gasto se ve en el tablero.</Text>}>
            <KeyValue label="Uso">Búsqueda de inmobiliarias por zona, desde la línea de comandos.</KeyValue>
          </Integracion>

          <Integracion titulo="Planilla de Google" ok={Boolean(i.sheets.url)} pie={<Text size="xs" tone="muted">Variable: <Var>GOOGLE_SHEETS_ID</Var>. Se actualiza con <Var>pnpm cli sheets:publicar</Var>.</Text>}>
            <KeyValue label="Planilla">{i.sheets.url ? <a href={i.sheets.url} target="_blank" rel="noreferrer">Abrir en Google Sheets</a> : "–"}</KeyValue>
          </Integracion>

          <Integracion titulo="Redacción con Claude" ok={i.claude.configurado} estadoNo="Sólo plantilla" pie={<Text size="xs" tone="muted">Variables: <Var>ANTHROPIC_API_KEY</Var> y <Var>CLAUDE_MODEL</Var>.</Text>}>
            <KeyValue label="Modelo" mono>
              {i.claude.modelo ?? "–"}
            </KeyValue>
            <KeyValue label="Uso">La primera línea de cada borrador, personalizada por inmobiliaria.</KeyValue>
          </Integracion>

          <Integracion titulo="Email" ok={false} estadoNo="En pausa" pie={<Text size="xs" tone="muted">Variables: <Var>RESEND_API_KEY</Var>, <Var>EMAIL_REMITENTE</Var>. Hoy no se manda email: lo decide el equipo.</Text>}>
            <KeyValue label="Resend">{i.resend.configurado ? "Clave cargada" : "Sin clave"}</KeyValue>
            <KeyValue label="Remitente">{sesion.remitente}</KeyValue>
          </Integracion>

          <Integracion titulo="Acceso al panel" ok={sesion.usuario !== "local"} estadoOk="Con usuario y clave" estadoNo="Sólo esta máquina" pie={<Text size="xs" tone="muted">Variable: <Var>PANEL_USUARIOS</Var>, con la forma <Var>email:clave,otro:clave</Var>. Siempre detrás de HTTPS. La sesión dura 30 días; cambiar una clave cierra todas. La firma de los WhatsApp es el usuario con mayúscula; para tildes o nombres compuestos, <Var>PANEL_NOMBRES</Var> con la forma <Var>tobias:Tobías</Var>.</Text>}>
            <KeyValue label="Entraste como">{sesion.usuario === "local" ? "Acceso local, sin usuario" : sesion.usuario}</KeyValue>
            <KeyValue label="Firmás los WhatsApp como">{sesion.firma}</KeyValue>
            <KeyValue label="URL pública" mono>
              {i.servidor.baseUrl ?? "–"}
            </KeyValue>
          </Integracion>
        </div>
      </Page>
    </>
  );
}
